// src/products/analysis/MarketDataApp.js
import React from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Search, Briefcase, Coins, Package, BarChart3, Building2, Flame, Factory, Megaphone, LineChart } from 'lucide-react';
import { useGlobalStore } from '../../state/globalStore';

// Import Components
import Sidebar from '../../components/layout/Sidebar';
import SharedHeader from '../../components/layout/SharedHeader';

// Import Pages
import FindCompanyPage from './pages/FindCompanyPage';
import PortfolioPage from './pages/PortfolioPage';
import StockChartPage from './pages/StockChartPage';
import NewsPage from './pages/NewsPage';
import CryptoPage from './pages/CryptoPage';
import CommoditiesPage from './pages/CommoditiesPage';
import PrivateCompaniesPage from './pages/PrivateCompaniesPage';
import CrowdfundingPage from './pages/CrowdfundingPage';
import EnergyMarketsPage from './pages/EnergyMarketsPage';
import CryptoCorrelationPage from './pages/CryptoCorrelationPage';
import TreasuryBondsPage from './pages/TreasuryBondsPage';
import PortfolioAnalysisPage from './pages/PortfolioAnalysisPage';

const MarketDataApp = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useGlobalStore();

  const getActivePage = () => {
    const path = location.pathname;
    if (path.includes('/find-company')) return '/market-data/find-company';
    if (path.includes('/portfolio/analysis')) return '/market-data/portfolio/analysis';
    if (path.includes('/portfolio')) return '/market-data/portfolio';
    if (path.includes('/crypto/correlation')) return '/market-data/crypto/correlation';
    if (path.includes('/crypto')) return '/market-data/crypto';
    if (path.includes('/energy')) return '/market-data/energy';
    if (path.includes('/commodities')) return '/market-data/commodities';
    if (path.includes('/treasury')) return '/market-data/treasury';
    if (path.includes('/private-companies')) return '/market-data/private-companies';
    if (path.includes('/crowdfunding')) return '/market-data/crowdfunding';
    return '/market-data/find-company'; // Default
  };

  // Navigation items for MarketData
  const navItems = [
    { id: '/market-data/find-company', icon: Search, label: 'Find Company' },
    { id: '/market-data/private-companies', icon: Factory, label: 'Private Filings' },
    { id: '/market-data/crowdfunding', icon: Megaphone, label: 'Crowdfunding' },
    { id: '/market-data/portfolio', icon: Briefcase, label: 'Saved Portfolio' },
    { id: '/market-data/portfolio/analysis', icon: LineChart, label: 'Portfolio Analysis' },
    { id: '/market-data/crypto', icon: Coins, label: 'Cryptocurrency' },
    { id: '/market-data/crypto/correlation', icon: BarChart3, label: 'Crypto Correlation' },
    { id: '/market-data/energy', icon: Flame, label: 'Energy Markets' },
    { id: '/market-data/commodities', icon: Package, label: 'Commodities' },
    { id: '/market-data/treasury', icon: Building2, label: 'Treasury & Bonds' }
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar
        items={navItems}
        activeItem={getActivePage()}
        onItemClick={(itemId) => navigate(itemId)}
        productName="MarketData"
        user={user}
        userProfile={userProfile}
        onBackToProducts={() => window.location.href = '/my-account/dashboard'}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <SharedHeader
          user={user}
          userProfile={userProfile}
          userRole={null}
          productName="MarketData"
        />
        
        {/* Main Content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 p-6">
          <Routes>
            <Route path="find-company" element={<FindCompanyPage />} />
            <Route path="portfolio" element={<PortfolioPage />} />
            <Route path="portfolio/analysis" element={<PortfolioAnalysisPage />} />
            <Route path="crypto/correlation" element={<CryptoCorrelationPage />} />
            <Route path="crypto" element={<CryptoPage />} />
            <Route path="energy" element={<EnergyMarketsPage />} />
            <Route path="commodities" element={<CommoditiesPage />} />
            <Route path="treasury" element={<TreasuryBondsPage />} />
            <Route path="private-companies" element={<PrivateCompaniesPage />} />
            <Route path="crowdfunding" element={<CrowdfundingPage />} />
            <Route path="chart/:symbol" element={<StockChartPage />} />
            <Route path="news/:symbol" element={<NewsPage />} />
            <Route index element={<FindCompanyPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default MarketDataApp;

